// The Android channel is injected only into the configured HTTPS tools origin.
// Frame data remains in memory; never upload it or place it in browser storage.
export const hasNativePhotoCamera = () => typeof window.ZlzlUsbCamera?.postMessage === 'function';
export const isNativePhotoCamera = camera => camera.mode === 'usb' && camera.deviceId?.startsWith('uvc:');
let nextId = 0;
let boundChannel;
const pending = new Map();

export function nativePhotoRequest(action, params = {}) {
  if (!hasNativePhotoCamera()) return Promise.reject(new Error('NATIVE_CAMERA_UNAVAILABLE'));
  const channel = window.ZlzlUsbCamera;
  if (boundChannel !== channel) {
    boundChannel = channel;
    channel.onmessage = event => {
      let reply;
      try { reply = JSON.parse(event.data); } catch { return; }
      const waiting = pending.get(reply.id);
      if (!waiting) return;
      pending.delete(reply.id);
      clearTimeout(waiting.timer);
      if (reply.ok) waiting.resolve(reply);
      else waiting.reject(new Error(reply.error || 'NotReadableError'));
    };
  }
  return new Promise((resolve, reject) => {
    const id = ++nextId;
    const timer = setTimeout(() => {
      pending.delete(id); reject(new Error('CAMERA_TIMEOUT'));
    }, 4000);
    pending.set(id, { resolve, reject, timer });
    try { channel.postMessage(JSON.stringify({ ...params, id, action })); }
    catch (error) { clearTimeout(timer); pending.delete(id); reject(error); }
  });
}

export async function listPhotoCameras() {
  const browser = await Promise.resolve(navigator.mediaDevices?.enumerateDevices?.()).catch(() => []);
  const native = hasNativePhotoCamera() ? (await nativePhotoRequest('list')).devices : [];
  return [...(native || []), ...(browser || []).filter(device => device.kind === 'videoinput')];
}

/** Adapt decoded native frames to the existing video/canvas capture pipeline. */
export function openNativePhotoCamera(deviceId, onError) {
  const session = `photo-${Date.now()}-${++nextId}`;
  const canvas = document.createElement('canvas');
  canvas.width = 640; canvas.height = 480;
  let stopped = false;
  let stream;
  let timer;
  let decodeTimeout;
  let image;
  let sequence = -1;
  let settle;
  let first = true;
  const ready = new Promise((resolve, reject) => { settle = { resolve, reject }; });
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    clearTimeout(decodeTimeout);
    if (image) { image.onload = null; image.onerror = null; image.removeAttribute('src'); image = null; }
    stream?.getTracks().forEach(track => track.stop());
    canvas.width = 0; canvas.height = 0;
    settle.reject(new Error('CAMERA_DISCONNECTED'));
    nativePhotoRequest('stop', { session }).catch(() => {}); // native watchdog also releases abandoned sessions
  };
  const fail = error => {
    if (stopped) return;
    settle.reject(error);
    stop();
    onError(error);
  };
  const poll = async () => {
    try {
      const result = await nativePhotoRequest('frame', { session, sequence });
      if (stopped) return;
      if (result.frame) {
        image = new Image();
        image.onload = () => {
          if (stopped) return;
          clearTimeout(decodeTimeout);
          try {
            canvas.getContext('2d').drawImage(image, 0, 0, 640, 480);
            image.removeAttribute('src'); image = null;
            sequence = result.sequence;
            if (first) { first = false; settle.resolve(stream); }
            timer = setTimeout(poll, 100);
          } catch (error) { fail(error); }
        };
        image.onerror = () => fail(new Error('NotReadableError'));
        decodeTimeout = setTimeout(() => fail(new Error('CAMERA_TIMEOUT')), 3000);
        image.src = result.frame;
      } else timer = setTimeout(poll, 100);
    } catch (error) { fail(error); }
  };
  (async () => {
    try {
      if (!canvas.captureStream) throw new Error('NATIVE_CAMERA_UNAVAILABLE');
      stream = canvas.captureStream(10);
      await nativePhotoRequest('start', { session, deviceId });
      if (stopped) { nativePhotoRequest('stop', { session }).catch(() => {}); return; }
      poll();
    } catch (error) { fail(error); }
  })();
  return { ready, stop };
}
